import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboardCheck, faPaperPlane, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import { normalizeTaskProgressPercent, resolveTaskProgressState, TASK_PRIORITY_OPTIONS, TASK_STATUS_OPTIONS, toDateInputValue } from "../taskUtils";

const buildDefaultValues = ({ ticket, identity, employees, initialValues }) => {
  if (initialValues) {
    return {
      title: initialValues.title || "",
      description: initialValues.description || "",
      priority: initialValues.priority || "Medium",
      status: initialValues.status || "Pending",
      progressPercent: normalizeTaskProgressPercent(initialValues.progressPercent),
      dueDate: initialValues.dueDate ? String(initialValues.dueDate).split("T")[0] : "",
      assignedEmployeeId: String(initialValues.assignedEmployeeId || ""),
    };
  }

  const selfEmployee = (employees || []).find((item) => String(item.userId || "") === String(identity?.userId || ""));

  return {
    title: ticket?.subject ? `Follow up: ${ticket.subject}` : "",
    description: ticket?.description
      ? `Task generated from support ticket ${ticket.ticketNumber || ""}.\n\nCustomer issue:\n${ticket.description}`
      : "",
    priority: ticket?.priority || "Medium",
    status: "Pending",
    progressPercent: 0,
    dueDate: toDateInputValue(new Date(Date.now() + 24 * 60 * 60 * 1000)),
    assignedEmployeeId: selfEmployee?.id ? String(selfEmployee.id) : "",
  };
};

export default function TaskComposerModal({
  open,
  ticket,
  employees,
  identity,
  initialValues,
  submitting,
  mode = "create",
  onClose,
  onSubmit,
}) {
  const [formState, setFormState] = useState(() => buildDefaultValues({ ticket, identity, employees, initialValues }));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setFormState(buildDefaultValues({ ticket, identity, employees, initialValues }));
    setErrors({});
  }, [open, ticket, identity, employees, initialValues]);

  const assignee = useMemo(
    () => (employees || []).find((item) => String(item.id) === String(formState.assignedEmployeeId || "")) || null,
    [employees, formState.assignedEmployeeId]
  );
  const canEditProgress = mode !== "edit" || String(initialValues?.assignedUserId || "") === String(identity?.userId || "");

  if (!open) return null;

  const validate = () => {
    const nextErrors = {};

    if (!formState.title.trim()) nextErrors.title = "Task title is required.";
    if (!formState.description.trim()) nextErrors.description = "Task details are required.";
    if (!formState.assignedEmployeeId) nextErrors.assignedEmployeeId = "Choose an employee.";
    if (!formState.dueDate) nextErrors.dueDate = "Due date is required.";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    onSubmit({
      ...resolveTaskProgressState(formState.status, formState.progressPercent),
      title: formState.title.trim(),
      description: formState.description.trim(),
      priority: formState.priority,
      dueDate: formState.dueDate,
      assignedEmployeeId: Number(formState.assignedEmployeeId || 0),
      assignedUserId: assignee?.userId || "",
      assignedEmployeeName: assignee?.fullName || "",
      assignedEmployeeEmail: assignee?.workEmail || "",
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-0 sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 flex min-h-[100dvh] w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl sm:min-h-0 sm:max-h-[92vh] sm:rounded-3xl">
        <div className="border-b border-slate-200 bg-[linear-gradient(135deg,#0f172a,#1e293b)] px-4 py-4 text-white sm:px-6 sm:py-5">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 sm:right-5 sm:top-5"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>

          <div className="pr-12">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-100">
              <FontAwesomeIcon icon={faClipboardCheck} />
              {mode === "edit" ? "Update Task" : "Make Task"}
            </div>
            <h3 className="mt-3 text-lg font-semibold sm:text-xl">
              {mode === "edit" ? "Update task details" : "Create professional task from this ticket"}
            </h3>
            <p className="mt-2 text-sm text-slate-300">
              {ticket?.ticketNumber ? `Linked ticket: ${ticket.ticketNumber}` : "Task workspace"}
            </p>
          </div>
        </div>

        <div className="grid flex-1 gap-4 overflow-y-auto p-4 sm:gap-5 sm:p-6 md:grid-cols-2">
          <Field label="Task Title" required error={errors.title}>
            <input
              value={formState.title}
              onChange={(event) => setFormState((current) => ({ ...current, title: event.target.value }))}
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
              placeholder="Example: Verify invoice issue and call customer"
            />
          </Field>

          <Field label="Assign To" required error={errors.assignedEmployeeId}>
            <select
              value={formState.assignedEmployeeId}
              onChange={(event) => setFormState((current) => ({ ...current, assignedEmployeeId: event.target.value }))}
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
            >
              <option value="">Select employee</option>
              {(employees || []).map((employee) => (
                <option key={employee.id || employee.userId} value={employee.id}>
                  {employee.fullName}
                  {employee.employeeCode ? ` (${employee.employeeCode})` : ""}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Priority">
            <select
              value={formState.priority}
              onChange={(event) => setFormState((current) => ({ ...current, priority: event.target.value }))}
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
            >
              {TASK_PRIORITY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Status">
            <select
              value={formState.status}
              onChange={(event) =>
                setFormState((current) => {
                  const nextStatus = event.target.value;
                  const progressState = resolveTaskProgressState(
                    nextStatus,
                    nextStatus === "Completed" ? 100 : current.progressPercent
                  );

                  return {
                    ...current,
                    status: progressState.status,
                    progressPercent: progressState.progressPercent,
                  };
                })
              }
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
            >
              {TASK_STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Progress">
            <div className="space-y-3">
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={formState.progressPercent}
                disabled={!canEditProgress}
                onChange={(event) =>
                  setFormState((current) => {
                    const progressState = resolveTaskProgressState(current.status, event.target.value);
                    return {
                      ...current,
                      status: progressState.status,
                      progressPercent: progressState.progressPercent,
                    };
                  })
                }
                className={`w-full accent-slate-900 ${!canEditProgress ? "cursor-not-allowed opacity-60" : ""}`}
              />
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formState.progressPercent}
                  disabled={!canEditProgress}
                  onChange={(event) =>
                    setFormState((current) => {
                      const progressState = resolveTaskProgressState(current.status, event.target.value);
                      return {
                        ...current,
                        status: progressState.status,
                        progressPercent: progressState.progressPercent,
                      };
                    })
                  }
                  className={`w-24 rounded-2xl border px-4 py-3 text-sm outline-none transition ${!canEditProgress ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400" : "border-slate-200 focus:border-slate-400"}`}
                />
                <span className="text-sm font-medium text-slate-500">Percent complete</span>
              </div>
              <div className="text-xs text-slate-500">
                {canEditProgress
                  ? "When progress reaches 100%, the task is marked completed automatically."
                  : "Only the active task person can change progress."}
              </div>
            </div>
          </Field>

          <Field label="Due Date" required error={errors.dueDate}>
            <input
              type="date"
              value={formState.dueDate}
              onChange={(event) => setFormState((current) => ({ ...current, dueDate: event.target.value }))}
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
            />
          </Field>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Created By</div>
            <div className="mt-2 font-medium text-slate-900">{identity?.fullName || identity?.email || "Logged in user"}</div>
            <div className="mt-1 text-xs text-slate-500">{identity?.userId || "No user ID"}</div>
          </div>

          <div className="md:col-span-2">
            <Field label="Task Details" required error={errors.description}>
              <textarea
                value={formState.description}
                onChange={(event) => setFormState((current) => ({ ...current, description: event.target.value }))}
                className="min-h-[180px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
                placeholder="Add the work details, customer context, expected result, and next step."
              />
            </Field>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-4 py-4 sm:flex-row sm:justify-end sm:px-6 sm:py-5">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-full border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-white sm:w-auto sm:py-2.5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-70 sm:w-auto sm:py-2.5"
          >
            <FontAwesomeIcon icon={submitting ? faSpinner : faPaperPlane} spin={submitting} />
            {submitting ? "Saving..." : mode === "edit" ? "Update Task" : "Create Task"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </label>
      {children}
      {error ? <div className="mt-2 text-xs font-medium text-rose-600">{error}</div> : null}
    </div>
  );
}
